import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../redux/errors.slice"
import { type RootState } from "../../../store.ts"
import type { BaseApiResponse } from "../../../types/api.ts"
import { showNotification } from "../../../utils/notification/show-notification"
import {
  selectActiveBranchId,
  selectIsCompanyMode,
  selectPageFilter
} from "../../Branches/redux/branches.slice.ts"
import * as services from "../api/services.ts"
import type { GetEventsParams } from "../api/services.types.ts"
import { setStoredEventColor } from "../CalendarPage/helpers/event-custom-colors.ts"
import type { Event } from "../types/types.ts"
import { calendarSetProp } from "./calendar.slice.ts"

// Never both at once — a single-branch workspace's own scope, or Company
// HQ's multi-select filter — mirrors `InvoicesPage.tsx`'s identical
// precedence exactly, so the two features can never disagree about what
// "team"/"teams" mean on the wire.
export const calendarBranchScopeParams = (
  state: RootState
): { team?: string; teams?: string } => {
  const isCompanyMode = selectIsCompanyMode(state)
  const activeBranchId = selectActiveBranchId(state)
  if (!isCompanyMode) return activeBranchId ? { team: activeBranchId } : {}

  const pageBranchScope = selectPageFilter("calendar")(state)
  return pageBranchScope.type === "subset"
    ? { teams: pageBranchScope.teamIds.join(",") }
    : {}
}

export const fetchEvents = createAsyncThunk(
  "calendar/fetchEvents",
  async (
    params: Partial<GetEventsParams> | undefined,
    { dispatch, getState, rejectWithValue }
  ) => {
    const state = getState() as RootState
    const filters = state?.calendar?.filters
    const { participants, ...restFilters } = filters

    const response = await services.getEvents({
      ...restFilters,
      participants: participants?.map((p) => p?.value)?.join(",") || null,
      ...calendarBranchScopeParams(state),
      ...params
    })

    if (!response.ok) {
      dispatch(errorSave((response.data as unknown as BaseApiResponse<unknown>)?.details))
      return rejectWithValue(response.data)
    }

    return response.data
  }
)

export const createCalendarEvent = createAsyncThunk(
  "calendar/createEvent",
  async (params: any, { dispatch, rejectWithValue }) => {
    // The backend computes background/border/text color from category —
    // there is no writable color field on the model, so a custom pick never
    // reaches the API. It's persisted client-side, keyed by the id the
    // create response hands back, once that id actually exists.
    const { custom_color, ...payload } = params
    const response = await services.createEvent(payload)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }
    const conflict_warning = response?.data?.details?.data?.conflict_warning

    if (conflict_warning) {
      showNotification.warning({
        message: "Schedule conflict",
        description:
          "One or more participants already have another scheduled event at the selected time."
      })
    }

    setStoredEventColor((response.data as { id?: string })?.id, custom_color)

    dispatch(fetchEvents({ page_size: 100 }))
    dispatch(calendarSetProp({ key: "addModalData", value: null }))

    const message =
      params?.type?.toLowerCase() === "reminder"
        ? "Reminder created successfully."
        : "Event created successfully."

    showNotification.success({ message })

    return response.data
  }
)

export const fetchEventDetails = createAsyncThunk(
  "calendar/fetchEventDetails",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.getEventDetails(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data
  }
)

export const updateCalendarEvent = createAsyncThunk(
  "calendar/updateEvent",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.updateEvent(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    dispatch(fetchEvents({ page_size: 100 }))

    return response.data
  }
)

export const partialUpdateCalendarEvent = createAsyncThunk<
  any,
  Pick<Event, "id"> & Partial<Omit<Event, "id">>
>(
  "calendar/partialUpdateEvent",
  async (params, { dispatch, getState, rejectWithValue }) => {
    // See createCalendarEvent — the backend has no writable color field, so
    // this never reaches the API; it's persisted client-side instead. The
    // id is already known on an edit, unlike on create.
    const { custom_color, ...payload } = params as typeof params & {
      custom_color?: string | null
    }
    const response = await services.partialUpdateEvent(payload)
    const moreEventsData = (getState() as RootState)?.calendar?.moreEventsData

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    setStoredEventColor(params?.id, custom_color)

    dispatch(fetchEvents({ page_size: 100 }))
    dispatch(calendarSetProp({ key: "selectedEvent", value: null }))

    if (moreEventsData) {
      dispatch(calendarSetProp({ key: "moreEventsData", value: null }))
    }

    const conflict_warning = response?.data?.details?.data?.conflict_warning

    if (conflict_warning) {
      showNotification.warning({
        message: "Schedule conflict",
        description:
          "One or more participants already have another scheduled event at the selected time."
      })
    }

    const message =
      params?.type?.toLowerCase() === "reminder"
        ? "Reminder updated successfully."
        : "Event updated successfully."

    showNotification.success({ message })

    return response.data
  }
)

export const updateReminderStatus = createAsyncThunk(
  "calendar/updateReminderStatus",
  async (params: any, { dispatch, getState, rejectWithValue }) => {
    const response = await services.partialUpdateEvent(params)
    const state = (getState() as RootState)?.calendar

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    dispatch(fetchEvents({ page_size: 100 }))
    dispatch(calendarSetProp({ key: "selectedEvent", value: null }))

    if (state?.moreEventsData) {
      dispatch(calendarSetProp({ key: "moreEventsData", value: null }))
    }

    const message = params?.is_completed
      ? "Reminder marked as completed."
      : "Reminder marked as uncompleted."

    showNotification.success({ message })

    return response.data
  }
)

export const deleteCalendarEvent = createAsyncThunk(
  "calendar/deleteEvent",
  async (params: any, { dispatch, getState, rejectWithValue }) => {
    const response = await services.deleteEvent(params)
    const moreEventsData = (getState() as RootState)?.calendar?.moreEventsData

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    dispatch(fetchEvents({ page_size: 100 }))
    dispatch(calendarSetProp({ key: "selectedEvent", value: null }))

    if (moreEventsData) {
      dispatch(calendarSetProp({ key: "moreEventsData", value: null }))
    }

    const message =
      params?.type === "reminder"
        ? "Reminder deleted successfully."
        : "Event deleted successfully."

    showNotification.success({ message })

    return { ...response.data, id: params }
  }
)

export const fetchEventAttachments = createAsyncThunk(
  "calendar/fetchEventAttachments",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.getEventAttachments(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data
  }
)

export const createCalendarEventAttachment = createAsyncThunk(
  "calendar/createEventAttachment",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.createEventAttachment(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data
  }
)

export const completeCalendarEvent = createAsyncThunk(
  "calendar/completeEvent",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.completeEvent(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    dispatch(fetchEvents({ page_size: 100 }))

    return response.data
  }
)

export const updateDraggableEvent = createAsyncThunk<
  any,
  Pick<Event, "id"> & Partial<Omit<Event, "id">>
>(
  "calendar/updateDragbleEvent",
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.partialUpdateEvent(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    const conflict_warning = response?.data?.details?.data?.conflict_warning

    if (conflict_warning) {
      showNotification.warning({
        message: "Schedule conflict",
        description:
          "One or more participants already have another scheduled event at the selected time."
      })
    }

    const message =
      params?.type?.toLowerCase() === "reminder"
        ? "Reminder updated successfully."
        : "Event updated successfully."

    showNotification.success({ message })

    dispatch(fetchEvents({ page_size: 100 }))

    return response.data
  }
)
